import React, { useState, useEffect, useRef } from 'react';
import { View, Button, Flex } from '@adobe/react-spectrum';
import { QuillEditor } from './QuillEditor';
import { ValidationTooltip } from './ValidationTooltip';
import { FormField as StoreFormField } from '../../store/slice';
import styles from '../FormField.module.scss';
import { fetchDropdownOptions } from '../../store/api';
import EditIn from '@spectrum-icons/workflow/EditIn';
import { MultiSelectDropdownComponent } from './MultiSelectDropdownComponent';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { productHierarchyService } from '../../services/productHierarchyService';
import { getTimezoneAbbreviation } from '../../../../../utils/time';

interface DropdownOption {
  id: string;
  name: string;
}

interface MultiSelectWithRichTextEditorProps {
  field: StoreFormField;
  value: { options: string[]; content: string };
  onChange: (name: string, value: any) => void;
  slideout?: boolean;
  formValues?: Record<string, any>;
  isInvalid?: boolean;
}

export const MultiSelectWithRichTextEditor: React.FC<
  MultiSelectWithRichTextEditorProps
> = ({
  field,
  value = { options: [], content: '' },
  onChange,
  slideout,
  formValues = {},
  isInvalid
}) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date>(() => new Date());
  const [charCount, setCharCount] = useState(0);
  const allAudiencesRef = useRef<DropdownOption[] | null>(null);
  const lastAppliedproductsRef = useRef<string | null>(null);

  // Update charCount when content changes
  useEffect(() => {
    // Remove trailing newline if present
    const contentLength = value.content
      ? value.content.length - (value.content.endsWith('\n') ? 1 : 0)
      : 0;
    setCharCount(contentLength);
  }, [value.content]);

  // Fetch dropdown options
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Special handling for product-hierarchy-filters to mirror customerAudiences
      if (field.api === 'product-hierarchy-filters') {
        setIsLoading(true);
        try {
          const products = formValues?.products;

          // No product selected: show full audience list and auto-select all
          if (!products) {
            if (!allAudiencesRef.current) {
              const all = await productHierarchyService.fetchAllAudiences();
              allAudiencesRef.current = all.map(item => ({
                id: item,
                name: item
              }));
            }
            if (!cancelled) {
              const fullList = allAudiencesRef.current || [];
              setOptions(fullList);
            }
            setIsLoading(false);
            return;
          }

          // Product selected: fetch dependent subset and auto-select those, while keeping full list as options
          const dependent = await productHierarchyService.fetchDependentOptions(
            products
          );

          if (!allAudiencesRef.current) {
            const all = await productHierarchyService.fetchAllAudiences();
            allAudiencesRef.current = all.map(item => ({
              id: item,
              name: item
            }));
          }
          const fullList = allAudiencesRef.current || [];
          const subsetValues = (dependent.customerAudiences || []).map(
            item => item
          );

          if (!cancelled) {
            setOptions(fullList);
            // Only auto-apply when products changes (preserve user's manual selections otherwise)
            const currentKey = Array.isArray(products)
              ? JSON.stringify(products)
              : String(products);
            if (lastAppliedproductsRef.current !== currentKey) {
              lastAppliedproductsRef.current = currentKey;
              const current = value?.options || [];
              const merged = Array.from(
                new Set([...(current || []), ...subsetValues])
              );
              const sameLength = merged.length === current.length;
              const currentSet = new Set(current);
              const isSame = sameLength && merged.every(v => currentSet.has(v));
              if (!isSame) {
                onChange(field.name, {
                  options: merged,
                  content: value?.content || ''
                });
              }
            }
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      // Default behavior: API or static options
      if (field.api) {
        setIsLoading(true);
        fetchDropdownOptions(field.api)
          .then(data => {
            if (!cancelled) {
              setOptions(data);
            }
          })
          .finally(() => {
            if (!cancelled) setIsLoading(false);
          });
      } else if (field.options) {
        const formattedOptions = field.options.map(opt => ({
          id: opt.value,
          name: opt.label
        }));
        setOptions(formattedOptions);
      } else {
        setOptions([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [field.api, field.options, field.name, formValues?.products]);

  // Handle multi-select change
  const handleMultiSelectChange = (
    fieldName: string,
    newSelected: string[]
  ) => {
    onChange(field.name, { options: newSelected, content: value.content });
  };

  // Handle rich text editor change
  const handleEditorChange = (content: string) => {
    onChange(field.name, { options: value.options, content });
  };

  // Format the last saved timestamp
  const formatTimestamp = (date: Date) => {
    const dateString = date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    // Use our cross-browser compatible timezone abbreviation
    const timezone = getTimezoneAbbreviation();
    return `Saved: ${dateString} ${timezone}`;
  };

  const selected = value.options || [];

  return (
    <View
      UNSAFE_className={styles.dropdownWithRichTextContainer}
      data-testid="multi-select-rich-text-editor"
    >
      {slideout ? (
        <div
          className={styles.slideoutFieldGroup}
          data-testid="slideout-field-group"
        >
          <div
            className={styles.slideoutCheckboxRow}
            data-testid="slideout-checkbox-row"
          >
            {options.map(opt => (
              <div
                key={opt.id}
                className={`${styles.slideoutCheckbox} ${
                  selected.includes(opt.id) ? styles.selected : ''
                }`}
                onClick={() => {
                  const newSelected = selected.includes(opt.id)
                    ? selected.filter(s => s !== opt.id)
                    : [...selected, opt.id];
                  handleMultiSelectChange(field.name, newSelected);
                }}
                tabIndex={0}
                role="button"
                aria-pressed={selected.includes(opt.id)}
                data-testid={`slideout-checkbox-${opt.id}`}
              >
                {selected.includes(opt.id) && (
                  <span
                    className={styles.checkmark}
                    data-testid={`checkbox-checkmark-${opt.id}`}
                  >
                    <BlackCheckMarkIcon width={14} height={14} />
                  </span>
                )}
                {opt.name}
              </div>
            ))}
          </div>
          <div
            className={styles.richTextSection}
            data-testid="rich-text-section"
          >
            <QuillEditor
              value={value.content}
              onChange={handleEditorChange}
              placeholder="Enter details here..."
              charLimit={
                typeof (field as any)?.charLimit === 'number' ||
                (field as any)?.charLimit === null
                  ? (field as any).charLimit
                  : null
              }
              showCharCount={true}
              className={styles.fullEditor}
              onCharCountChange={setCharCount}
              minHeight="100px"
              footerLeft="Characters remaining"
              data-testid="quill-editor"
            />
            <div className={styles.editorFooterRow} data-testid="editor-footer">
              <span
                className={styles.savedTimestamp}
                data-testid="saved-timestamp"
              >
                {formatTimestamp(lastSaved)}
              </span>
              <button
                className={styles.doneButton}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
                onClick={e => {
                  e.stopPropagation();
                  setLastSaved(new Date());
                }}
                aria-label="Done"
                data-testid="done-button"
              >
                <BlackCheckMarkIcon width={24} height={24} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <View
          UNSAFE_className={styles.dropdownContainer}
          data-testid="dropdown-container"
        >
          <MultiSelectDropdownComponent
            field={
              {
                ...field,
                // Provide resolved options to the dropdown to avoid duplicated fetching logic
                api: undefined,
                options: options.map(opt => ({
                  value: opt.id,
                  label: opt.name
                }))
              } as any
            }
            value={value.options}
            onChange={handleMultiSelectChange}
            formValues={formValues}
            showCheckbox={false}
            data-testid="multi-select-dropdown"
          />

          <ValidationTooltip
            message="Please select at least one option from the multi-select dropdown first"
            isDisabled={selected.length === 0}
          >
            <Button
              variant="primary"
              UNSAFE_className={`${styles.editorToggleButtonLarge} ${
                isEditorVisible
                  ? styles.activeToggleButton
                  : styles.editorTOggleButton
              } ${isInvalid ? 'invalid' : ''}`}
              onPress={() => setIsEditorVisible(!isEditorVisible)}
              isDisabled={selected.length === 0}
              data-testid="editor-toggle-button"
            >
              <EditIn />
            </Button>
          </ValidationTooltip>
        </View>
      )}
      <Flex UNSAFE_className={styles.richTextSection}>
        {selected.length > 0 && isEditorVisible && (
          <>
            <div
              className={styles.editorContainer}
              data-testid="editor-container"
            >
              <QuillEditor
                value={value.content}
                onChange={handleEditorChange}
                placeholder="Enter details here..."
                charLimit={
                  typeof (field as any)?.charLimit === 'number' ||
                  (field as any)?.charLimit === null
                    ? (field as any).charLimit
                    : null
                }
                showCharCount={true}
                className={styles.fullEditor}
                onCharCountChange={setCharCount}
                minHeight="100px"
                footerLeft="Characters remaining"
                data-testid="expanded-quill-editor"
              />
            </div>
          </>
        )}
      </Flex>
    </View>
  );
};
